/* eslint-disable max-len, jsx-a11y/anchor-is-valid */
import * as React from 'react';
import Link from 'next/link';
import { ChevronRight, Phone } from 'lucide-react'; // Phone used in desktop button only
import { cn } from '@/util/cn';
import { Heading } from '@/components/ui/heading';
import { Text } from '@/components/ui/text';
import { SubNav, type SubNavItem } from '@/components/common/SubNav';
import { PartnerVideo } from '@/components/resort/PartnerVideo';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

// Shared header for resort-level production pages. Composes:
//   1. Breadcrumb (Country › Region › Resort)
//   2. H1 — the `heading` the caller supplies (e.g. "Vail Mountain Webcams")
//   3. Optional Nearby chip row — hidden on mobile
//   4. Optional OnTheSnow+ Partner block (`sponsored` prop). Renders
//      above the title on mobile; to the right of the title at lg+
//      (ribbon + a narrow stacked Website / Phone button pair, plus the
//      promo video thumbnail to its right — desktop only).
//   5. SubNav tabs — full-bleed on mobile, pill-capsule on md+

export type ResortHeaderNearby = {
  name: string;
  href?: string;
};

export type ResortHeaderSponsored = {
  /** Resort website URL — opens in a new tab. */
  website?: string;
  /** Phone number; used as both the `tel:` href and the visible label. */
  phone?: string;
  /** YouTube URL for the resort's promo video. Rendered as a small poster
   *  thumbnail right of the partner block at lg+; opens in a dialog on
   *  click. Hidden below lg. */
  video?: string;
};

export type ResortHeaderProps = {
  country: string;
  region: string;
  resortName: string;
  /**
   * The complete H1 text. Comes from the page's SEO hero title
   * (`hero.title` in the page's `.server.ts`), which carries the head term
   * the template ranks for — "Telluride Ski Resort", not "Telluride Guide".
   * Falls back to the bare resort name.
   */
  heading?: string;
  /** Optional nearby resort list rendered between H1 and SubNav. */
  nearby?: ResortHeaderNearby[];
  subNav: SubNavItem[];
  /** Link href for breadcrumb country + region segments. Defaults to '#'. */
  breadcrumbHref?: string;
  /** When set, renders the OnTheSnow+ Partner block. At least one of
   *  website/phone/video must be populated; otherwise the block hides. */
  sponsored?: ResortHeaderSponsored;
  className?: string;
};

const Crumbs = ({
  country, region, resortName, href = '#',
}: {
  country: string;
  region: string;
  resortName: string;
  href?: string;
}) => (
  <nav className="mb-3 flex flex-wrap items-center gap-1 text-xs text-muted-foreground md:text-sm">
    <Link href={href} className="hover:text-foreground">{country}</Link>
    <ChevronRight size={14} className="fill-none text-muted-foreground/60" />
    <Link href={href} className="hover:text-foreground">{region}</Link>
    <ChevronRight size={14} className="fill-none text-muted-foreground/60" />
    <span className="font-medium text-foreground">{resortName}</span>
  </nav>
);

const NearbyChips = ({ items }: { items: ResortHeaderNearby[] }) => {
  const { t } = useOTSTranslation('common');
  return (
    <div className="mb-6 hidden flex-wrap items-center gap-2 md:flex">
      <Text variant="subtle" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t('common.nearbyResorts')}
      </Text>
      {items.map((n) => (
        <Link
          key={n.name}
          href={n.href ?? '#'}
          className="inline-flex items-center rounded-full border border-border bg-background px-3 py-1 text-xs font-semibold text-foreground transition hover:border-primary/40 hover:bg-sky-50 hover:text-primary"
        >
          {n.name}
        </Link>
      ))}
    </div>
  );
};

// OnTheSnow+ Partner block. Ribbon (desktop only) + a compact Website /
// Phone button pair sitting side by side in a single row at every width.
const PartnerBlock = ({
  website, phone, video,
}: ResortHeaderSponsored) => {
  const { t } = useOTSTranslation('common');
  // The thumbnail overlaps this block's right edge at lg+ (its lg:-ml-5,
  // 20px), so the block must guarantee that what gets covered is the
  // ribbon and never the phone number. Two things make that work:
  //   - the ribbon takes lg:min-w-full so it spans the whole block and
  //     therefore always reaches under the thumbnail, whatever the
  //     translated label measures;
  //   - the button row reserves lg:pr-7 (28px) on its right, which must
  //     stay >= the overlap, so the buttons always stop short of the
  //     thumbnail. In en-US the ribbon and the phone button otherwise end
  //     within a pixel of each other, and which one is wider flips by
  //     locale, so this clearance is structural rather than a tuned width.
  // The overlap also has to beat the clipPath below, whose last 12px taper
  // to the point is empty space: an overlap of 12px or less is swallowed
  // entirely by the taper and reads as a gap rather than a bleed.
  // All of it only when there is a thumbnail — otherwise the ribbon would
  // stretch past its text for no reason.
  const overlapsVideo = !!video;
  return (
    // Width is intentionally intrinsic at lg+ (no fixed w-*). The ribbon
    // and the button labels are all translated strings, so their combined
    // width varies per locale and any fixed width overflows in some of
    // them. That overflow used to be invisible because nothing sat to the
    // block's right; the promo thumbnail now does, and clipped it.
    // lg:pt-1.5 drops the whole block ~5px so the ribbon doesn't sit
    // exactly tangent to the thumbnail's top edge. Padding on the block
    // rather than a margin on the ribbon keeps the ribbon-to-buttons
    // spacing intact, and leaves the thumbnail — a sibling flex item —
    // where it is.
    <div className="lg:w-auto lg:shrink-0 lg:pt-1.5">
      {/* Ribbon — desktop only. Right-pointing flag with a notch cut into
          the left edge, flush to the block's left edge. Kept in normal
          flow so it contributes its width to the block above. */}
      <div
        className={cn(
          'hidden w-fit items-center bg-secondary py-1.5 pl-5 pr-7 text-xs font-bold uppercase tracking-widest text-white shadow-md lg:inline-flex',
          overlapsVideo && 'lg:min-w-full',
        )}
        style={{ clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%, 10px 50%)' }}
      >
        {t('common.premierPartner')}
      </div>

      {/* pt-2 at lg reproduces the 8px gap the old absolute ribbon left
          behind its 36px pt-9 offset. */}
      <div className={cn('lg:pt-2', overlapsVideo && 'lg:pr-7')}>
        {/* Buttons — a compact side-by-side row at every width. Mobile
            uses muted colors to feel like supporting metadata, not a
            primary CTA — and drops the phone icon for visual quiet.
            Desktop keeps the icon and a sharper border. */}
        {(website || phone) && (
          <div className="flex flex-row gap-2 max-md:text-[0.6875rem]">
            {website && (
              <a
                href={website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 whitespace-nowrap rounded-full border border-border/60 bg-background px-3 py-1.5 text-center font-bold uppercase tracking-wider text-muted-foreground transition hover:border-primary hover:text-primary md:border-border md:text-xs md:tracking-widest md:text-foreground lg:flex-none lg:text-[0.6875rem]"
              >
                {t('common.website')}
              </a>
            )}
            {phone && (
              <a
                href={`tel:${phone}`}
                className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-border/60 bg-background px-3 py-1.5 font-bold tabular-nums text-muted-foreground transition hover:border-primary hover:text-primary md:border-border md:text-xs md:text-foreground lg:flex-none lg:text-[0.6875rem]"
              >
                <Phone size={13} className="hidden fill-none text-primary md:inline-block" />
                {phone}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const ResortHeader = ({
  country,
  region,
  resortName,
  heading,
  nearby,
  subNav,
  breadcrumbHref,
  sponsored,
  className,
}: ResortHeaderProps) => {
  // Empty sponsored objects (e.g. isPartner=true but no website/phone/
  // video) shouldn't render an orphan ribbon.
  const hasSponsored = !!(sponsored?.website || sponsored?.phone || sponsored?.video);

  return (
    <header className={cn('mb-2 max-md:mb-6', className)}>
      {/* Title row. On mobile the partner block sits above the title
          via order-first; at lg+ it moves to the right column. */}
      <div
        className={cn(
          'flex flex-col lg:flex-row lg:items-start lg:gap-8',
          hasSponsored && 'lg:justify-between',
        )}
      >
        {hasSponsored && (
          // Below lg: a column with the thumbnail centred above the
          // Website / Phone row. At lg+: a row, thumbnail to the right of
          // the block with its top flush to the ribbon's, pulled back far
          // enough that the badge visibly runs under it. The horizontal
          // inset lives here rather than on PartnerBlock so both children
          // share it.
          // lg:pb-6 keeps the thumbnail — the tallest thing in this
          // column, so it sets the header row's height — off the SubNav
          // that follows immediately after the row.
          <div className="order-first mb-6 flex flex-col gap-3 max-md:px-8 lg:order-last lg:mb-0 lg:flex-row lg:items-start lg:gap-0 lg:px-0 lg:pb-6">
            {sponsored?.video && (
              <PartnerVideo
                url={sponsored.video}
                // Centred in the stack on mobile; at lg+ pulled back over
                // the ribbon, with z-10 so it paints over it, not under.
                className="max-lg:mx-auto lg:relative lg:z-10 lg:order-last lg:-ml-5"
              />
            )}
            <PartnerBlock {...sponsored!} />
          </div>
        )}
        <div className="min-w-0 flex-1 max-md:px-4">
          <Crumbs country={country} region={region} resortName={resortName} href={breadcrumbHref} />
          <Heading
            level={1}
            className="mb-4 font-heading text-2xl text-secondary md:text-4xl lg:text-5xl"
          >
            {heading || resortName}
          </Heading>
          {nearby && nearby.length > 0 && <NearbyChips items={nearby} />}
        </div>
      </div>
      {/* SubNav stays inset by the parent container's horizontal padding —
          sits visually flush with the body content beneath it. */}
      <SubNav items={subNav} />
    </header>
  );
};
